import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { progressAPI, reportsAPI, bookmarksAPI } from '../../services/api';
import ImageLightbox from './ImageLightbox';
import part1Questions from '../../data/questions/part1.json';
import part2Questions from '../../data/questions/part2.json';
import part3Questions from '../../data/questions/part3.json';
import part4Questions from '../../data/questions/part4.json';
import part5Questions from '../../data/questions/part5.json';
import part6Questions from '../../data/questions/part6.json';
import part7Questions from '../../data/questions/part7.json';
import part8Questions from '../../data/questions/part8.json';
import part9Questions from '../../data/questions/part9.json';
import part10Questions from '../../data/questions/part10.json';

const questionData = {
  '1': part1Questions,
  '2': part2Questions,
  '3': part3Questions,
  '4': part4Questions,
  '5': part5Questions,
  '6': part6Questions,
  '7': part7Questions,
  '8': part8Questions,
  '9': part9Questions,
  '10': part10Questions
};

function Assessment() {
  const { partNumber } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState({});
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
  const [lightboxImage, setLightboxImage] = useState(null);
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    // Load questions for the current part
    const partData = questionData[partNumber];
    if (partData) {
      setQuestions(partData.questions);
    }
    // Load saved progress from API
    progressAPI.getPart(partNumber)
      .then(data => {
        if (data.answers) {
          setAnswers(data.answers);
        }
      })
      .catch(() => {});
    // Load bookmarks
    bookmarksAPI.getAll()
      .then(data => {
        if (data.bookmarks) {
          setBookmarkedQuestions(new Set(data.bookmarks.map(b => b.question_id)));
        }
      })
      .catch(() => {});
  }, [partNumber, user]);

  // Modal accessibility: ESC key handler and focus trap
  useEffect(() => {
    if (!showReportModal) return;

    // Store the previously focused element
    previousActiveElement.current = document.activeElement;

    // Focus the modal when it opens
    if (modalRef.current) {
      const firstFocusable = modalRef.current.querySelector('textarea, button');
      if (firstFocusable) firstFocusable.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowReportModal(false);
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, textarea, input, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when modal closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [showReportModal]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const handleOptionClick = (optionIndex) => {
    if (showFeedback) return;
    setSelectedAnswer(optionIndex);
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: {
        selected: selectedAnswer,
        correct: isCorrect
      }
    };
    setAnswers(newAnswers);

    // Save to API
    try {
      await progressAPI.saveAnswer(partNumber, currentQuestion.id, selectedAnswer, isCorrect);
    } catch (err) {
      console.error('Failed to save answer:', err);
    }

    setShowFeedback(true);
  };

  const handleNext = async () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    } else {
      // Calculate and save completion
      const score = Object.values(answers).filter(a => a.correct).length +
        (selectedAnswer === currentQuestion.correctAnswer ? 1 : 0);

      try {
        await progressAPI.completePart(partNumber, score, totalQuestions);
      } catch (err) {
        console.error('Failed to save completion:', err);
      }

      setShowSummary(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      const prevQuestion = questions[currentIndex - 1];
      const prevAnswer = answers[prevQuestion.id];
      if (prevAnswer) {
        setSelectedAnswer(prevAnswer.selected);
        setShowFeedback(true);
      } else {
        setSelectedAnswer(null);
        setShowFeedback(false);
      }
    }
  };

  const handleReportQuestion = () => {
    setShowReportModal(true);
    setReportComment('');
  };

  const submitReport = async () => {
    try {
      await reportsAPI.submit(
        currentQuestion.id,
        partNumber,
        currentQuestion.stem,
        reportComment
      );

      setReportSubmitted({ ...reportSubmitted, [currentQuestion.id]: true });
      setShowReportModal(false);
      setReportComment('');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  const toggleBookmark = async () => {
    const questionId = currentQuestion.id;
    const isBookmarked = bookmarkedQuestions.has(questionId);

    try {
      if (isBookmarked) {
        await bookmarksAPI.remove(questionId);
        setBookmarkedQuestions(prev => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      } else {
        await bookmarksAPI.add(questionId, parseInt(partNumber, 10));
        setBookmarkedQuestions(prev => new Set(prev).add(questionId));
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  };

  if (!currentQuestion) {
    return <div className="loading">Loading questions...</div>;
  }

  if (showSummary) {
    const correctCount = Object.values(answers).filter(a => a.correct).length;
    const percentage = Math.round((correctCount / totalQuestions) * 100);

    return (
      <div className="assessment">
        <div className="score-summary">
          <h2>Part {partNumber} Complete!</h2>
          <div className="score-display">{correctCount}/{totalQuestions}</div>
          <div className="score-percentage">{percentage}%</div>
          <p style={{ marginBottom: '30px', color: '#666' }}>
            {percentage >= 80 ? 'Excellent work!' :
             percentage >= 60 ? 'Good effort. Review the missed questions.' :
             'Consider reviewing the material and trying again.'}
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isCorrect = showFeedback && selectedAnswer === currentQuestion.correctAnswer;
  const optionLetters = ['A', 'B', 'C', 'D'];

  return (
    <div className="assessment">
      <div className="assessment-header">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Part {partNumber}: {questionData[partNumber]?.partTitle}</h1>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
        <p style={{ marginTop: '10px', color: '#666' }}>
          Question {currentIndex + 1} of {totalQuestions}
        </p>
      </div>

      <div className="question-card">
        <div className="question-header-row">
          <div className="question-number">Question {currentIndex + 1}</div>
          <button
            className={`bookmark-btn ${bookmarkedQuestions.has(currentQuestion.id) ? 'active' : ''}`}
            onClick={toggleBookmark}
            title={bookmarkedQuestions.has(currentQuestion.id) ? 'Remove bookmark' : 'Bookmark for review'}
            aria-label={bookmarkedQuestions.has(currentQuestion.id) ? 'Remove bookmark' : 'Bookmark this question for review'}
          >
            {bookmarkedQuestions.has(currentQuestion.id) ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
            )}
          </button>
        </div>
        <div className="question-stem">{currentQuestion.stem}</div>

        {currentQuestion.images && currentQuestion.images.length > 0 && (
          <div className="question-images">
            {currentQuestion.images.map((image, idx) => (
              <figure key={idx} className="question-image-container">
                <img
                  src={image.url}
                  alt={image.alt || `Question image ${idx + 1}`}
                  className="question-image"
                  onClick={() => setLightboxImage(image)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxImage(image)}
                />
                {image.caption && (
                  <figcaption className="question-image-caption">{image.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        <div className="options" role="radiogroup" aria-label="Answer options">
          {currentQuestion.options.map((option, index) => {
            let className = 'option';
            const isSelected = index === selectedAnswer;
            const isCorrectAnswer = index === currentQuestion.correctAnswer;
            const isIncorrect = showFeedback && isSelected && !isCorrectAnswer;

            if (showFeedback) {
              className += ' disabled';
              if (isCorrectAnswer) {
                className += ' correct';
              } else if (isIncorrect) {
                className += ' incorrect';
              }
            } else if (isSelected) {
              className += ' selected';
            }

            return (
              <button
                key={index}
                type="button"
                className={className}
                onClick={() => handleOptionClick(index)}
                disabled={showFeedback}
                role="radio"
                aria-checked={isSelected}
                aria-label={`Option ${optionLetters[index]}: ${option}${showFeedback && isCorrectAnswer ? ' (Correct answer)' : ''}`}
              >
                <span className="option-letter" aria-hidden="true">{optionLetters[index]}.</span>
                <span className="option-text">{option}</span>
              </button>
            );
          })}
        </div>
      </div>

      {!showFeedback && (
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={selectedAnswer === null}
          style={{ opacity: selectedAnswer === null ? 0.5 : 1 }}
        >
          Submit Answer
        </button>
      )}

      {showFeedback && (
        <div className={`feedback-panel ${isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
          <div className="feedback-header">
            <span className="feedback-icon">{isCorrect ? '✓' : '✗'}</span>
            <strong>{isCorrect ? 'Correct!' : 'Incorrect'}</strong>
          </div>

          <div className="rationale">
            <strong>Explanation:</strong> {currentQuestion.rationale}
          </div>

          <div className="source-excerpt">
            <h4>Source Material</h4>
            <blockquote>"{currentQuestion.sourceExcerpt}"</blockquote>
            <cite>— {currentQuestion.sourceLocation}</cite>
          </div>

          <div className="report-question">
            {reportSubmitted[currentQuestion.id] ? (
              <span className="report-submitted">Thank you for your feedback</span>
            ) : (
              <button className="btn-report" onClick={handleReportQuestion} title="Report an issue with this question">
                <span className="thumbs-down">👎</span> Report Issue
              </button>
            )}
          </div>
        </div>
      )}

      {showReportModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowReportModal(false)}
          role="presentation"
        >
          <div
            ref={modalRef}
            className="modal-content"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
            aria-describedby="report-modal-desc"
          >
            <h3 id="report-modal-title">Report Question Issue</h3>
            <p id="report-modal-desc" className="modal-question-id">Question: {currentQuestion.id}</p>
            <textarea
              placeholder="Describe the issue (incorrect answer, unclear wording, typo, etc.)"
              value={reportComment}
              onChange={e => setReportComment(e.target.value)}
              rows={4}
              aria-label="Describe the issue with this question"
            />
            <div className="modal-buttons">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowReportModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitReport}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="navigation-buttons">
          <button
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            style={{ opacity: currentIndex === 0 ? 0.5 : 1 }}
          >
            ← Previous
          </button>
          <button className="btn btn-primary" onClick={handleNext}>
            {currentIndex < totalQuestions - 1 ? 'Next Question →' : 'Finish Part'}
          </button>
        </div>
      )}

      {lightboxImage && (
        <ImageLightbox
          image={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}

export default Assessment;
